import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';
import { QrReader } from 'react-qr-reader';

// --- Interfaces ---
interface LookupResponse {
  serialNumber: string;
  isActivated: boolean;
  customerName?: string;
  customerPhone?: string;
  warrantyExpires?: string;
  device?: {
    name_vi?: string;
    name_en?: string;
    manualPdf?: string;
    thumbnail?: string;
  };
}

// --- Component #1: Form Nhập S/N Thủ Công + Quét QR ---
const SerialNumberForm = () => {
  const [serial, setSerial] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { data } = await api.get(`/serials/lookup/${serial.trim().toUpperCase()}`);
      navigate(`/vi/kiem-tra-san-pham/lookup/${data.serialNumber}`);
    } catch {
      setError('Số sê-ri không hợp lệ hoặc không tồn tại. Vui lòng thử lại.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = (err: any) => {
    console.error('Lỗi khi sử dụng camera:', err);
    alert('Không thể truy cập camera. Vui lòng cấp quyền hoặc thử lại.');
  };

  return (
    <div className="text-center">
      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">
        Kiểm tra thông tin sản phẩm
      </h1>
      <p className="mt-4 text-lg text-gray-600">
        Nhập hoặc quét mã sê-ri (Serial Number) in trên sản phẩm của bạn để xem chi tiết.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 max-w-lg mx-auto space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            placeholder="Ví dụ: MERY01ANCHABC"
            required
            className="flex-grow px-4 py-3 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <button
            type="button"
            onClick={() => setShowScanner((prev) => !prev)}
            className="px-4 py-3 bg-green-700 text-white font-semibold rounded-md hover:bg-green-800 transition-colors"
          >
            {showScanner ? 'Đóng' : 'Quét QR'}
          </button>
        </div>

        {showScanner && (
            <div className="mt-4 mx-auto w-full max-w-sm border-2 border-green-500 rounded-md overflow-hidden">
                <QrReader
    constraints={{ facingMode: 'environment' }}
    containerStyle={{ width: '100%' }}
    // ✅ THÊM !important ĐỂ KIỂM TRA
    videoStyle={{ 
        width: '100% !important', 
        height: '100% !important' 
    }}
    onResult={(result) => {
        if (result) {
            setSerial(result.getText());
            setShowScanner(false);
        }
    }}
/>
            </div>
            )}


        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full px-6 py-3 bg-green-700 text-white font-semibold rounded-md hover:bg-green-800 transition-colors disabled:bg-green-400"
        >
          {isLoading ? 'Đang kiểm tra...' : 'Kiểm tra'}
        </button>
      </form>
    </div>
  );
};

// --- Component #2: Giao diện Hiển thị Thông tin Đã Kích hoạt ---
const ProductInfoView = ({ data }: { data: LookupResponse }) => (
  <div className="flex flex-col gap-8">
    <div className="text-center">
      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">
        {data.device?.name_vi || 'Không xác định'}
      </h1>
      <p className="text-gray-500 mt-2">
        Số Sê-ri: <strong>{data.serialNumber}</strong>
      </p>
    </div>
    <div className="bg-green-100 border-l-4 border-green-500 text-green-800 p-4 rounded-md">
      <p className="font-bold">Sản phẩm đã được kích hoạt bảo hành!</p>
      {data.warrantyExpires && (
        <p>
          Hạn bảo hành đến hết ngày:{' '}
          {new Date(data.warrantyExpires).toLocaleDateString('vi-VN')}
        </p>
      )}
    </div>
    <div className="bg-gray-100 border border-gray-300 p-4 rounded-md">
      <h3 className="font-bold text-lg text-gray-800">Thông tin khách hàng đã đăng ký</h3>
      <div className="mt-2 text-gray-700">
        <p>
          <strong>Tên:</strong> {data.customerName}
        </p>
        <p>
          <strong>SĐT:</strong> {data.customerPhone}
        </p>
      </div>
    </div>
    {data.device?.manualPdf && (
      <a
        href={data.device.manualPdf}
        target="_blank"
        rel="noopener noreferrer"
        className="text-center w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700"
      >
        Xem Hướng dẫn sử dụng (PDF)
      </a>
    )}
  </div>
);

// --- Component #3: Giao diện Kích hoạt Lần đầu ---
const RegistrationView = ({
  data,
  onActivated,
}: {
  data: LookupResponse;
  onActivated: () => void;
}) => {
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await api.put(`/serials/activate/${data.serialNumber}`, {
        customerName,
        customerPhone,
      });
      alert('Kích hoạt sản phẩm thành công!');
      onActivated();
    } catch {
      alert('Kích hoạt thất bại. Sản phẩm có thể đã được kích hoạt bởi người khác.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">Kích hoạt sản phẩm</h1>
        <p className="text-gray-500 mt-2">
          Sản phẩm: <strong>{data.device?.name_vi}</strong> | Sê-ri:{' '}
          <strong>{data.serialNumber}</strong>
        </p>
      </div>
      <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-800 p-4 rounded-md mb-8">
        <p className="font-bold">Đây là lần quét đầu tiên!</p>
        <p>Vui lòng nhập thông tin khách hàng để kích hoạt bảo hành.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="customerName" className="block text-sm font-medium text-gray-700">
            Tên khách hàng
          </label>
          <input
            type="text"
            id="customerName"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            required
            className="mt-1 block w-full p-2 border rounded"
          />
        </div>
        <div>
          <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-700">
            Số điện thoại
          </label>
          <input
            type="tel"
            id="customerPhone"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            required
            className="mt-1 block w-full p-2 border rounded"
          />
        </div>
        <button
          type="submit"
          disabled={isSaving}
          className="w-full px-4 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-blue-300"
        >
          {isSaving ? 'Đang kích hoạt...' : 'Kích hoạt bảo hành'}
        </button>
      </form>
    </>
  );
};

// --- Component Chính ---
const KiemTraSanPham = () => {
  const { serialNumber } = useParams<{ serialNumber?: string }>();
  const [lookupData, setLookupData] = useState<LookupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const performLookup = async (sn: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/serials/lookup/${sn}`);
      setLookupData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Đã xảy ra lỗi không xác định.');
      setLookupData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (serialNumber) performLookup(serialNumber);
    else setIsLoading(false);
  }, [serialNumber]);

  const renderContent = () => {
    if (isLoading) return <p className="text-center">Đang kiểm tra thông tin...</p>;
    if (error)
      return (
        <div className="text-center">
          <h1 className="text-3xl font-bold text-red-600">Lỗi</h1>
          <p className="text-lg text-gray-700 mt-4">{error}</p>
        </div>
      );
    if (lookupData)
      return lookupData.isActivated ? (
        <ProductInfoView data={lookupData} />
      ) : (
        <RegistrationView
          data={lookupData}
          onActivated={() => performLookup(lookupData.serialNumber)}
        />
      );
    return <SerialNumberForm />;
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12 flex items-center">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="bg-white rounded-lg shadow-xl p-6 md:p-8">{renderContent()}</div>
      </div>
    </div>
  );
};

export default KiemTraSanPham;